// absolute imports
import { useMutation, useQuery, useReactiveVar } from '@apollo/client';
import {
  useLocation,
  useNavigate,
  useParams,
  useRouteContext,
} from '@tanstack/react-router';
import PropTypes from 'prop-types';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

// relative imports
import {
  adminViewingClientVar,
  creatingAccountVar,
  currentAccountConfigVar,
  currentAccountIdVar,
  currentAccountTaxYearVar,
  currentAccountTimezoneVar,
  lastAccessVar,
  primaryAccountIdVar,
  switchingAccountsVar,
} from '../../data/apollo/cache/reactiveVars';
import { CREATE_TAX_PAYER_ACCOUNT } from '../../data/apollo/mutations';
import {
  ACCOUNT,
  ACCOUNTS,
  GET_DEFAULT_ACCOUNT_ID_FOR_APPLICATION_TYPE,
} from '../../data/apollo/queries';
import { getAppConfig } from '../../data/app-config/configs/configUtilities';
import useUpdatePolling from '../shared/custom-hooks/useUpdatePolling';
import {
  disabledText,
  generateClientPermissions,
} from '../shared/permissions/PermissionRef';
import { getAccountDetailsForCreation } from '../shared/utilities/appUtilities';
import { guessTimezone } from '../shared/utilities/dateUtilities';
import { getUserIdFromAuth0Sub, useLedgibleAuth } from './LedgibleAuthProvider';

export const AccountContext = createContext();
export const useAccount = () => {
  const context = useContext(AccountContext);
  // Default context object if context is undefined
  // Note: Providers always render, but provides safety net during potential race conditions (e.g. logout/cache clearing)
  if (!context) {
    return {
      currentAccount: {},
      accounts: [],
      loadingAccountInfo: true, // Show loading as safety net if context unavailable
      clientPermissions: {},
      selectAccount: () => {},
      createAccount: () => {},
      refetchAccounts: () => {},
      creatingAccount: false,
      refetchCurrentAccount: () => {},
      needsApplicationTypeSelection: false,
      selectedApplicationTypeId: null,
      setSelectedApplicationTypeId: () => {},
      loadingApplicationTypeSelection: false,
      applicationTypeSelectionError: null,
      showApplicationTypeSelection: () => {},
      cancelApplicationTypeSelection: () => {},
      manualSelectionTriggered: false,
    };
  }
  return context;
};

// MAIN COMPONENT
export const AccountProvider = ({ children }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isClientRoute = useMemo(
    () => pathname.startsWith('/client/'),
    [pathname],
  );
  const params = useParams({ strict: false });
  const { auth0 } = useRouteContext({
    from: '/_authenticated',
  });
  const appConfig = getAppConfig();
  const {
    user,
    applicationTypeIdForAccountCreation,
    isFederatedIdentity,
    refetchUser,
  } = useLedgibleAuth();

  const emailIsVerified =
    isFederatedIdentity || !!user?.profile?.email_verified;
  const currentAccountTimezone = useReactiveVar(currentAccountTimezoneVar);
  const primaryAccountId = useReactiveVar(primaryAccountIdVar);
  const creatingAccount = useReactiveVar(creatingAccountVar);
  const switchingAccounts = useReactiveVar(switchingAccountsVar);

  const [needsApplicationTypeSelection, setNeedsApplicationTypeSelection] =
    useState(false);
  const [selectedApplicationTypeId, setSelectedApplicationTypeId] =
    useState(null);
  const [manualSelectionTriggered, setManualSelectionTriggered] =
    useState(false);
  const [applicationTypeSelectionError, setApplicationTypeSelectionError] =
    useState(null);

  // Get cached applicationTypeId from lastAccessVar and validate it against availableAccountApplicationTypes
  const cachedApplicationTypeId = useMemo(() => {
    const { applicationTypeId, userId } = lastAccessVar();
    const userSub = getUserIdFromAuth0Sub(auth0?.user?.sub);

    // Only use cached applicationTypeId if it's for the current user
    if (userSub !== userId || !applicationTypeId) {
      return null;
    }

    // Validate that the cached applicationTypeId is still in the available applicationTypes
    const isStillAvailable = user?.availableAccountApplicationTypes?.some(
      (applicationType) => applicationType.id === applicationTypeId,
    );

    return isStillAvailable ? applicationTypeId : null;
  }, [auth0?.user?.sub, user?.availableAccountApplicationTypes]);

  // Determine which applicationTypeId to use for the query
  // Don't use cached value when user manually opened selection screen
  const applicationTypeIdForQuery =
    selectedApplicationTypeId ||
    (manualSelectionTriggered || needsApplicationTypeSelection
      ? null
      : cachedApplicationTypeId);

  // Query to get the default account ID for a selected applicationType
  const {
    data: {
      getDefaultAccountIdForApplicationType: accountIdFromApplicationType,
    } = {},
    loading: loadingDefaultAccountId,
  } = useQuery(GET_DEFAULT_ACCOUNT_ID_FOR_APPLICATION_TYPE, {
    variables: {
      applicationTypeId: applicationTypeIdForQuery,
    },
    skip: !applicationTypeIdForQuery || !!user?.defaultAccountId,
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const accountId = data?.getDefaultAccountIdForApplicationType;

      if (primaryAccountIdVar() !== accountId && !isClientRoute) {
        // Update lastAccessVar with the accountId and applicationTypeId
        const userSub = getUserIdFromAuth0Sub(auth0?.user?.sub);
        lastAccessVar({
          id: accountId,
          userId: userSub,
          applicationTypeId: applicationTypeIdForQuery,
        });

        // Set the primary account ID
        primaryAccountIdVar(accountId);

        // Clear any previous errors
        setApplicationTypeSelectionError(null);

        // Close the selection screen and reset manual trigger flag
        setNeedsApplicationTypeSelection(false);
        setManualSelectionTriggered(false);

        // Navigate to root route if necessary to ensure proper dashboard loading
        if (pathname !== '/') {
          navigate({ to: '/' });
        }
      }
    },
    onError: (error) => {
      console.error(
        'Error fetching default account ID for application type:',
        error,
      );
      setApplicationTypeSelectionError(
        error?.message || 'Failed to load account',
      );
      // Handle state for  retries and keeping selection page open
      setSelectedApplicationTypeId(null);
      setNeedsApplicationTypeSelection(true);
    },
  });

  // Determine the defaultAccountId to use
  const defaultAccountId = useMemo(() => {
    const { id, userId } = lastAccessVar();
    const userSub = getUserIdFromAuth0Sub(auth0?.user?.sub);

    // Use cached accountId if it's for the current user
    if (userSub === userId && id) {
      return id;
    }

    // Use accountId from application type query if available
    if (accountIdFromApplicationType) {
      return accountIdFromApplicationType;
    }

    // Fall back to user's defaultAccountId
    return user?.defaultAccountId;
  }, [auth0?.user?.sub, user?.defaultAccountId, accountIdFromApplicationType]);

  useEffect(() => {
    if (defaultAccountId && !primaryAccountIdVar()) {
      primaryAccountIdVar(defaultAccountId);
    }
  }, [defaultAccountId]);

  const {
    data: { getAccount: currentAccount = {} } = {},
    refetch: refetchCurrentAccount,
    networkStatus: currentAccountNetworkStatus,
  } = useQuery(ACCOUNT, {
    variables: {
      // can either be a client account (params) or the main account
      accountId: params.accountId || primaryAccountId,
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    skip: !emailIsVerified || (!primaryAccountId && !params.accountId),
    onError(err) {
      if (err?.message === "User doesn't have access" && !params.accountId) {
        primaryAccountIdVar(null);
        lastAccessVar({
          id: null,
          userId: null,
        });
      }
    },
    onCompleted(data) {
      const { getAccount: { id, applicationType } = {} } = data;

      if (id !== currentAccountIdVar()) {
        // currentAccountId is based on currentAccount value only
        currentAccountIdVar(id);
      }

      // Clear switching state if the account's config is already loaded
      // (AccountConfigProvider will clear it if a new config needs to be loaded)
      const currentConfig = currentAccountConfigVar();
      if (switchingAccounts && applicationType?.id === currentConfig?.id) {
        switchingAccountsVar(false);
      }

      // client to admin view or no accountId param
      if (!params.accountId && lastAccessVar().id !== id) {
        lastAccessVar({
          id,
          userId: user?.id,
        });
      }
    },
  });

  // TODO 09/23/25 -> turn this to paginated response for Account list in Account Window
  const {
    data: { getAccounts: accounts } = {},
    refetch: refetchAccounts,
    networkStatus: accountsNetworkStatus,
  } = useQuery(ACCOUNTS, {
    variables: {
      type: currentAccount?.type,
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    skip: !user || currentAccount?.type !== 'Accounting' || !primaryAccountId,
  });

  // Calculate if admin is viewing client based on route and account data
  const isAdminViewingClient = useMemo(() => {
    return (
      isClientRoute &&
      currentAccount.id !== primaryAccountId &&
      currentAccount.preparerLink?.id &&
      currentAccount?.preparerLink?.preparerAccountId === primaryAccountId
    );
  }, [
    currentAccount.id,
    currentAccount.preparerLink?.id,
    currentAccount.preparerLink?.preparerAccountId,
    isClientRoute,
    primaryAccountId,
  ]);

  // Sync the calculated value to the reactive variable for backward compatibility
  useEffect(() => {
    const currentReactiveValue = adminViewingClientVar();
    if (currentReactiveValue !== isAdminViewingClient) {
      adminViewingClientVar(isAdminViewingClient);
    }
  }, [isAdminViewingClient]);

  // Assign client permissions based for current account
  const clientPermissions = useMemo(() => {
    let permissions = { disabledText };

    if (user && currentAccount.id) {
      // set permissions on provider
      const { role = 'read_only', userId } = currentAccount?.permission || {};
      const isOwner = currentAccount.owner === userId;

      permissions = generateClientPermissions({
        addAccount: user.addAccount,
        isAdminViewingClient,
        role,
        isOwner,
      });
    }

    return permissions || {};
  }, [currentAccount, user, isAdminViewingClient]);

  useUpdatePolling({
    preset: 'account',
    updateCondition:
      currentAccount?.updating || currentAccount?.serialUpdateActive,
    onUpdateComplete: refetchCurrentAccount,
  });

  // mutation for in-app account creation
  const [
    createTaxPayerAccount,
    { called: createTaxPayerAccountCalled, loading: creatingTaxPayerAccount },
  ] = useMutation(CREATE_TAX_PAYER_ACCOUNT, {
    onCompleted: async (data) => {
      const { createTaxPayerAccount: newTaxAccount = {} } = data;
      if (newTaxAccount.id) {
        await refetchUser();
        primaryAccountIdVar(newTaxAccount.id);
        currentAccountIdVar(newTaxAccount.id);
        creatingAccountVar(false);
      }
    },
  });

  // function to call account creation mutation
  const createAccountCallback = useCallback(
    (args) => {
      creatingAccountVar(true);
      createTaxPayerAccount(args);
    },
    [createTaxPayerAccount],
  );

  // set app-wide ref for currentAccount from passed account ID (for changing primary account eg multi-account Accounting)
  const selectPrimaryAccountCallback = useCallback(
    (selectedAccountId) => {
      let newAccountId = null;

      if (primaryAccountId && primaryAccountId !== selectedAccountId) {
        switchingAccountsVar(true);
        // Navigate to root route when switching accounts to ensure proper loading
        if (!isClientRoute && pathname !== '/') {
          navigate({ to: '/' });
        }
      }
      newAccountId = selectedAccountId;

      // set newAccountId if valid or set to null if not so useEffect can handle
      primaryAccountIdVar(newAccountId);
    },
    [primaryAccountId, isClientRoute, pathname, navigate],
  );

  // handle scenarios requiring account creation
  useEffect(() => {
    if (
      user?.profile?.email_verified &&
      !defaultAccountId &&
      !creatingAccount &&
      !creatingTaxPayerAccount &&
      !isClientRoute &&
      !createTaxPayerAccountCalled &&
      !needsApplicationTypeSelection &&
      !selectedApplicationTypeId
    ) {
      const applicationTypeRequired = appConfig?.accountApplicationTypeRequired;

      // handle scenario where application type selection is required to set up account (e.g. old FIS model)
      if (applicationTypeRequired) {
        creatingAccountVar(true);
      }

      // else set account details and create automatically if sigining up from link code and user isn't selecting manually
      else if (applicationTypeIdForAccountCreation) {
        const accountDetails = getAccountDetailsForCreation({
          auth0Username: user?.profile?.username,
        });

        accountDetails.applicationTypeId = applicationTypeIdForAccountCreation;

        // create account without user action (current tax year or accounting)
        createAccountCallback({
          variables: accountDetails,
        });
      }
    }
  }, [
    user?.profile?.email_verified,
    creatingAccount,
    creatingTaxPayerAccount,
    createTaxPayerAccountCalled,
    appConfig?.accountApplicationTypeRequired,
    createAccountCallback,
    isClientRoute,
    applicationTypeIdForAccountCreation,
    defaultAccountId,
    user?.profile?.username,
    needsApplicationTypeSelection,
    selectedApplicationTypeId,
  ]);

  // Detect state for select account application type
  useEffect(() => {
    if (manualSelectionTriggered) {
      // Don't auto-close if user manually opened selection screen
      return;
    }

    if (
      user?.profile?.email_verified &&
      !defaultAccountId &&
      !creatingAccount &&
      !isClientRoute &&
      user?.availableAccountApplicationTypes?.length > 1
    ) {
      setNeedsApplicationTypeSelection(true);
    } else {
      setNeedsApplicationTypeSelection(false);
    }
  }, [
    user?.profile?.email_verified,
    defaultAccountId,
    creatingAccount,
    isClientRoute,
    user?.availableAccountApplicationTypes,
    manualSelectionTriggered,
  ]);

  // Keep tax year updated for tax accounts
  useEffect(() => {
    if (
      currentAccount.taxPayerDetails &&
      currentAccount?.taxPayerDetails?.taxYear !== currentAccountTaxYearVar()
    ) {
      currentAccountTaxYearVar(currentAccount.taxPayerDetails.taxYear);
    }
  }, [currentAccount.taxPayerDetails, currentAccount?.type]);

  // Ensure timezone availabilty for account app-wide
  useEffect(() => {
    // timezone on account dictates timezone displayed in app
    if (
      currentAccount?.timezone &&
      currentAccount.timezone !== currentAccountTimezone
    ) {
      currentAccountTimezoneVar(currentAccount.timezone);
    }
    // default to timezone read from machine
    if (!currentAccount?.timezone && !currentAccountTimezone) {
      currentAccountTimezoneVar(guessTimezone());
    }
  }, [currentAccount?.timezone, currentAccountTimezone]);

  const loadingAccountInfo = useMemo(() => {
    // ACCOUNT query skip conditions
    const accountQuerySkipped =
      !emailIsVerified || (!primaryAccountId && !params.accountId);

    // Only consider it loading if:
    // 1. Network status is loading OR
    // 2. Query is not skipped AND we don't have an account ID yet
    const loadingCurrentAccount =
      currentAccountNetworkStatus === 1 ||
      (!accountQuerySkipped && !currentAccount?.id);

    // Check if ACCOUNTS query is skipped
    const accountsQuerySkipped =
      !user || currentAccount?.type !== 'Accounting' || !primaryAccountId;

    // Only consider it loading if query is not skipped AND network status is loading
    const loadingAccountsList =
      !accountsQuerySkipped &&
      accountsNetworkStatus === 1 &&
      accounts?.length === 0;

    if (loadingAccountsList || loadingCurrentAccount || switchingAccounts) {
      return true;
    }
    return false;
  }, [
    accounts?.length,
    accountsNetworkStatus,
    currentAccount?.id,
    currentAccount?.type,
    currentAccountNetworkStatus,
    emailIsVerified,
    params.accountId,
    primaryAccountId,
    switchingAccounts,
    user,
  ]);

  // callbacks to handle application type selection
  const showApplicationTypeSelection = useCallback(() => {
    // Clear any previous selection
    setManualSelectionTriggered(true);
    setNeedsApplicationTypeSelection(true);
  }, []);

  const handleSetSelectedApplicationTypeId = useCallback(
    (applicationTypeId) => {
      setSelectedApplicationTypeId(applicationTypeId);

      setApplicationTypeSelectionError(null);
    },
    [],
  );

  const cancelApplicationTypeSelection = useCallback(() => {
    setManualSelectionTriggered(false);
    setNeedsApplicationTypeSelection(false);
  }, []);

  /*
   * True only once account resolution has genuinely settled with nothing to open:
   * a loaded user, no account in hand, and nothing left in flight that could still
   * produce one. Anything short of this is still loading -- on a first login the
   * createUser -> refetch -> permission -> defaultAccountId chain can easily
   * outlast a naive timeout, and treating that as "no account" flashes an error
   * screen at a user whose account is about to appear.
   */
  const noAccountAvailable =
    Boolean(user) &&
    !loadingAccountInfo &&
    !loadingDefaultAccountId &&
    !currentAccount?.id &&
    !defaultAccountId &&
    !primaryAccountId &&
    !creatingAccount &&
    !creatingTaxPayerAccount &&
    !needsApplicationTypeSelection;

  const accountState = useMemo(
    () => ({
      currentAccount: currentAccount || {},
      accounts: accounts || [],
      loadingAccountInfo,
      clientPermissions: clientPermissions || {},
      selectAccount: selectPrimaryAccountCallback,
      createAccount: createAccountCallback,
      refetchAccounts,
      creatingAccount: creatingTaxPayerAccount,
      refetchCurrentAccount,
      needsApplicationTypeSelection,
      selectedApplicationTypeId,
      setSelectedApplicationTypeId: handleSetSelectedApplicationTypeId,
      loadingApplicationTypeSelection: loadingDefaultAccountId,
      applicationTypeSelectionError,
      showApplicationTypeSelection,
      cancelApplicationTypeSelection,
      manualSelectionTriggered,
      noAccountAvailable,
    }),
    [
      currentAccount,
      accounts,
      loadingAccountInfo,
      clientPermissions,
      selectPrimaryAccountCallback,
      createAccountCallback,
      refetchAccounts,
      creatingTaxPayerAccount,
      refetchCurrentAccount,
      needsApplicationTypeSelection,
      selectedApplicationTypeId,
      handleSetSelectedApplicationTypeId,
      loadingDefaultAccountId,
      noAccountAvailable,
      applicationTypeSelectionError,
      showApplicationTypeSelection,
      cancelApplicationTypeSelection,
      manualSelectionTriggered,
    ],
  );

  return (
    <AccountContext.Provider value={accountState}>
      {children}
    </AccountContext.Provider>
  );
};

export const AccountConsumer = AccountContext.Consumer;

AccountProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
